import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useMutation } from '@apollo/client'
import { ACCEPT_INVITE } from '../graphql/mutations'
import toast from 'react-hot-toast'
import { Input } from '../components/ui/Input'
import { Button } from '../components/ui/Button'

const AcceptInvitePage = () => {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') || ''
  const navigate = useNavigate()

  const [name, setName] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [serverError, setServerError] = useState<string | null>(null)

  const [acceptInvite, { loading }] = useMutation(ACCEPT_INVITE, {
    onCompleted: () => {
      toast.success('Account activated! Please sign in.')
      navigate('/login', { replace: true })
    },
    onError: (error) => {
      setServerError(error.message || 'Invalid or expired invitation link')
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setServerError(null)
    if (!name.trim()) {
      toast.error('Please enter your name')
      return
    }
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters long')
      return
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match')
      return
    }
    acceptInvite({ variables: { token, name: name.trim(), password } })
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-seasalt py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="flex justify-center">
            <img src="/brand/logo.svg" alt="Curry Me POS" className="h-14 w-auto" />
          </div>
          <h2 className="mt-6 text-center font-primary text-h4 font-bold text-secondary-900">
            Welcome to Curry Me
          </h2>
          <p className="mt-2 text-center text-body text-secondary-600">
            Set up your account to get started
          </p>
        </div>

        {!token ? (
          <div className="text-center space-y-6">
            <p className="text-body text-secondary-700">
              This link is missing its invitation token. Please use the link from your
              invitation email, or ask your administrator to send a new invitation.
            </p>
            <Link to="/login" className="text-primary-600 hover:text-primary-700 font-medium">
              Go to sign in
            </Link>
          </div>
        ) : (
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            <div className="rounded-md shadow-sm -space-y-px">
              <div>
                <label htmlFor="name" className="sr-only">
                  Your name
                </label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  autoComplete="name"
                  required
                  className="rounded-t-lg"
                  placeholder="Your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={loading}
                />
              </div>
              <div>
                <label htmlFor="password" className="sr-only">
                  Password
                </label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  placeholder="Password (min. 6 characters)"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                />
              </div>
              <div>
                <label htmlFor="confirm-password" className="sr-only">
                  Confirm password
                </label>
                <Input
                  id="confirm-password"
                  name="confirm-password"
                  type="password"
                  autoComplete="new-password"
                  required
                  className="rounded-b-lg"
                  placeholder="Confirm password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={loading}
                />
              </div>
            </div>

            {serverError && (
              <div className="text-center space-y-2">
                <p className="text-sm text-red-600">{serverError}</p>
                <p className="text-sm text-secondary-600">
                  Ask your administrator to send a new invitation if the link has expired.
                </p>
              </div>
            )}

            <Button type="submit" isLoading={loading} className="w-full" size="lg">
              Activate account
            </Button>
          </form>
        )}
      </div>
    </div>
  )
}

export default AcceptInvitePage
